<div class="stock-container" role="region" [attr.aria-labelledby]="resolvedConfig.title ? 'stock-title' : null">

  <!-- HEADER -->
  <header class="stock-header" *ngIf="resolvedConfig.title">
    <div class="stock-title">
      <h2 id="stock-title">{{ resolvedConfig.title }}</h2>
    </div>
  </header>

  <!-- SEARCH / ACTIONS -->
  <div class="search-actions-row" role="toolbar" aria-label="Stock toolbar">

    <div class="search-state-group">
      <!-- SEARCH -->
      <div class="search-container">
        <label class="visually-hidden" for="stockSearch">
          {{ 'pages.stock.search' | translate }}
        </label>

        <input id="stockSearch" type="search" class="search-input" [(ngModel)]="searchTerm"
          (ngModelChange)="onSearch($event)" [attr.placeholder]="('pages.stock.search' | translate)" autocomplete="off"
          spellcheck="false" />

        <span class="search-icon" aria-hidden="true"></span>
      </div>

      <!-- STORE FILTER -->
      <div *ngIf="resolvedConfig.showStoreFilter" class="state-filter-container">
        <label class="visually-hidden" for="storeFilterSelect">
          {{ 'pages.stock.store' | translate }}
        </label>

        <select id="storeFilterSelect" class="state-filter-dropdown" [(ngModel)]="storeFilter"
          (change)="applyFilters()">
          <option *ngFor="let storeOption of possibleStores" [ngValue]="storeOption.value">
            {{ storeOption.label }}
          </option>
        </select>
      </div>
    </div>

    <!-- ACTIONS -->
    <div class="actions-group">
      <a *ngIf="resolvedConfig.showCreateButton" class="movements-history-link" routerLink="/stock-movements">
        {{ 'viewMovementsHistory' | translate }}
      </a>

      <div *ngIf="resolvedConfig.showCreateButton" class="dropdown-container">
        <button type="button" class="btn btn-primary action-button" (click)="toggleAddProductsMenu()"
          aria-haspopup="menu" [attr.aria-expanded]="showAddProductsMenu">
          <span class="btn-label">{{ 'pages.stock.addProducts.title' | translate }}</span>
          <img src="assets/icons/backoffice/down-arrow.svg" class="dropdown-icon" alt="" aria-hidden="true" />
        </button>

        <div class="action-dropdown" *ngIf="showAddProductsMenu" role="menu">
          <button type="button" class="dropdown-item" role="menuitem"
            (click)="openBulkUploadModal(); closeAddProductsMenu()">
            {{ 'pages.stock.addProducts.csvUpload' | translate }}
          </button>

          <button type="button" class="dropdown-item" role="menuitem"
            (click)="openAddManualy(); closeAddProductsMenu()">
            {{ 'pages.stock.addProducts.manual' | translate }}
          </button>
        </div>
      </div>

      <!-- ALERTS LINK -->
      <a *ngIf="resolvedConfig.showAlertsLink && isCenterAdmin" class="alerts-link" routerLink="/stock/alerts">
        <img src="/assets/icons/bell-icon.svg" alt="" class="alert-icon" aria-hidden="true" />
        <span class="alert-text">{{ 'pages.stock.stock_alerts.title' | translate }}</span>
      </a>
    </div>
  </div>

  <!-- EXPORT -->
  <div class="export-stock-section" *ngIf="resolvedConfig.showCreateButton">
    <button type="button" class="export-stock-btn" (click)="openExportStockDrawer()">
      <span class="export-text">{{ 'pages.stock.exportStock' | translate }}</span>
      <img src="/assets/icons/backoffice/download.svg" alt="" class="export-icon" aria-hidden="true" />
    </button>
  </div>

  <!-- LOADING -->
  <div class="loading-container" *ngIf="isLoading" aria-live="polite" aria-busy="true">
    <div class="loading-spinner" role="img" aria-label="Loading"></div>
    <p>{{ 'pages.stock.loading' | translate }}</p>
  </div>

  <!-- EMPTY -->
  <div class="empty-state" *ngIf="!isLoading && stock.length === 0" aria-live="polite">
    <p>{{ 'pages.stock.noRecords' | translate }}</p>
  </div>

  <!-- TABLE -->
  <div class="table-container" *ngIf="!isLoading && stock.length > 0">
    <table class="stock-table">
      <caption class="visually-hidden">
        {{ resolvedConfig.title || ('pages.stock.title' | translate) }}
      </caption>

      <thead class="table-head">
        <tr class="table-head-row">
          <th scope="col">{{ 'pages.stock.table.productCode' | translate }}</th>
          <th scope="col">{{ 'pages.stock.table.productName' | translate }}</th>
          <th scope="col">{{ 'pages.stock.table.brand' | translate }}</th>
          <th scope="col">{{ 'pages.stock.table.season' | translate }}</th>
          <th scope="col">{{ 'pages.stock.table.stock_total' | translate }}</th>
          <th scope="col">{{ 'pages.stock.table.reserved_stock' | translate }}</th>
          <th scope="col">{{ 'pages.stock.lots.expirationDate' | translate }}</th>
          <th *ngIf="resolvedConfig.showStoreFilter" scope="col">
            {{ 'pages.stock.table.warehouse' | translate }}
          </th>
          <th scope="col" class="actions-col">{{ 'pages.stock.table.actions' | translate }}</th>
        </tr>
      </thead>

      <tbody>
        <ng-container *ngFor="let item of stock; let i = index; trackBy: trackByStock">
          <!-- MAIN ROW -->
          <tr class="main-stock-row" [class.is-expanded]="expandedStockIndex === i" (click)="toggleStockLots(i)"
            (keydown)="onRowKeydown($event, i)" [attr.aria-expanded]="expandedStockIndex === i"
            [attr.aria-controls]="'stock-lots-' + i" role="button" tabindex="0">
            <td class="product-code" [attr.data-label]="('pages.stock.table.productCode' | translate)">
              {{ item.productCode }}
            </td>

            <td class="product-name-cell" [attr.data-label]="('pages.stock.table.productName' | translate)">
              <div class="product-name">{{ item.productName }}</div>
            </td>

            <td class="product-brand" [attr.data-label]="('pages.stock.table.brand' | translate)">
              {{ item.productBrand }}
            </td>

            <td class="product-season" [attr.data-label]="('pages.stock.table.season' | translate)">
              <app-season-icon [season]="item.season" size="small"></app-season-icon>
            </td>

            <td [attr.data-label]="('pages.stock.table.stock_total' | translate)">
              <span class="stock-pill" [ngClass]="getStockStatusClass(item.stockQuantity)" aria-label="Stock">
                {{ item.stockQuantity }}
              </span>
            </td>

            <td [attr.data-label]="('pages.stock.table.reserved_stock' | translate)">
              <span class="stock-pill reserved-stock-pill" aria-label="Reserved stock">
                {{ item.reservedStock }}
              </span>
            </td>

            <td class="product-dot" [attr.data-label]="('pages.stock.lots.expirationDate' | translate)">
              {{ item.oldestDOT }}
            </td>

            <td *ngIf="resolvedConfig.showStoreFilter" class="store-cell"
              [attr.data-label]="('pages.stock.table.warehouse' | translate)">
              <div class="store-name">{{ item.storeName }}</div>
            </td>

            <td class="actions actions-col" [attr.data-label]="('pages.stock.table.actions' | translate)">
              <button type="button" class="btn btn-primary add-lot-button" (click)="openAddManualy($event, item)"
                [attr.title]="('pages.stock.add_lot' | translate)">
                {{ 'pages.stock.add_lot' | translate }}
              </button>
            </td>
          </tr>

          <!-- LOTS ROW -->
          <tr *ngIf="expandedStockIndex === i" class="lots-row" [attr.id]="'stock-lots-' + i">
            <td [attr.colspan]="resolvedConfig.showStoreFilter ? 9 : 8" class="lots-container">
              <div class="lots-header">
                <h4 class="lots-title">{{ 'pages.stock.lots.title' | translate }}</h4>
              </div>

              <ng-container *ngIf="item.lots.length; else noLots">
                <div class="lots-table-wrapper">
                  <table class="lots-table">
                    <thead>
                      <tr>
                        <th scope="col">{{ 'pages.stock.lots.quantity' | translate }}</th>
                        <th scope="col">{{ 'pages.stock.lots.reserved_stock' | translate }}</th>
                        <th scope="col">{{ 'pages.stock.lots.price' | translate }}</th>
                        <th scope="col">{{ 'pages.stock.lots.expirationDate' | translate }}</th>
                        <th scope="col">{{ 'pages.stock.lots.locations' | translate }}</th>
                        <th scope="col" class="actions-col">{{ 'pages.stock.lots.actions' | translate }}</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr *ngFor="let lot of item.lots; trackBy: trackByLot">
                        <td class="lot-quantity" [attr.data-label]="('pages.stock.lots.quantity' | translate)">
                          <span class="stock-pill" [ngClass]="getStockStatusClass(lot.stockQuantity)">
                            {{ lot.stockQuantity }}
                          </span>
                        </td>

                        <td class="lot-quantity" [attr.data-label]="('pages.stock.lots.reserved_stock' | translate)">
                          <span class="stock-pill reserved-stock-pill">{{ lot.reservedStock }}</span>
                        </td>

                        <td class="lot-price" [attr.data-label]="('pages.stock.lots.price' | translate)">
                          {{ formatPrice(lot.price) }}
                        </td>

                        <td class="lot-dot" [attr.data-label]="('pages.stock.lots.expirationDate' | translate)">
                          {{ lot.dot }}
                        </td>

                        <td class="lot-location" [attr.data-label]="('pages.stock.lots.locations' | translate)">
                          {{ (lot.locations || []).join(', ') }}
                        </td>

                        <td class="lot-actions actions-col" [attr.data-label]="('pages.stock.lots.actions' | translate)"
                          (click)="$event.stopPropagation()">
                          <div class="lot-actions-container">
                            <button type="button" class="icon-button" (click)="editStockLot(lot.lotId)"
                              [attr.title]="('common.edit' | translate)">
                              <img src="../../../../../assets/icons/pencil-icon.svg" alt="" aria-hidden="true" />
                            </button>

                            <button type="button" class="icon-button" (click)="openTranfer(lot, item)"
                              [attr.title]="('pages.stock.edit.transfer_stock' | translate)">
                              <img src="/assets/icons/backoffice/arrow-up-right.svg" alt="" aria-hidden="true" />
                            </button>

                            <button type="button" class="icon-button is-danger" (click)="deleteStockLot(lot)"
                              [attr.title]="('common.delete' | translate)">
                              <img src="../../../../../assets/icons/trash-icon.svg" alt="" aria-hidden="true" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </ng-container>

              <ng-template #noLots>
                <div class="no-lots" aria-live="polite">
                  {{ 'pages.stock.lots.noLots' | translate }}
                </div>
              </ng-template>
            </td>
          </tr>
        </ng-container>
      </tbody>
    </table>
  </div>

  <!-- PAGINATION -->
  <div class="pagination-wrapper" *ngIf="totalPages > 1">
    <app-table-paginator [loading]="isLoading" [currentPage]="currentPage" [hideResume]="true" [pageAmount]="totalPages"
      (pageEmitter)="setPage($event)"></app-table-paginator>
  </div>
</div>